/**
 * Canonical CTA library — source of truth for wording & hierarchy.
 *
 * Matches §7 "CTA library" of docs/copywriting-style-guide.md. If you need a
 * new CTA slot, add it here first (and update the guide), then use it via
 * <CtaButton intent="..." />. Ad-hoc button labels for these actions are
 * forbidden by the CI test in src/__tests__/cta-library.test.ts.
 */

export type CtaIntent =
  | "requestAccess"
  | "seeDemo"
  | "statusLookup"
  | "signupResume"
  | "buddyInvite"
  | "newsletter";

export type CtaHierarchy = "primary" | "secondary" | "tertiary";

export interface CtaDefinition {
  /** Verbatim label from the style guide. */
  label: string;
  /** Optional microcopy shown under the button. */
  microcopy?: string;
  /** Loading-state label (present-continuous, lowercase-feel). */
  loadingLabel?: string;
  /**
   * Accessible name override. Only set when the visible label is ambiguous
   * without surrounding context. Most CTAs use the label directly.
   */
  ariaLabel?: string;
  /** Recommended hierarchy in a group (guides default variant). */
  defaultHierarchy: CtaHierarchy;
  /** Analytics id used by trackCtaClick. */
  analyticsId: string;
}

export const CTA_LIBRARY: Record<CtaIntent, CtaDefinition> = {
  requestAccess: {
    label: "Request access",
    microcopy: "Invite only · onboarding in small waves",
    loadingLabel: "Sending…",
    defaultHierarchy: "primary",
    analyticsId: "request_access",
  },
  seeDemo: {
    label: "See the demo",
    microcopy: "No account · three quiet questions",
    loadingLabel: "Opening…",
    defaultHierarchy: "secondary",
    analyticsId: "see_the_demo",
  },
  statusLookup: {
    label: "See where I stand",
    loadingLabel: "Looking…",
    defaultHierarchy: "primary",
    analyticsId: "status_lookup",
  },
  signupResume: {
    label: "Step inside",
    microcopy: "Myelina opens by invitation.",
    loadingLabel: "Opening…",
    defaultHierarchy: "primary",
    analyticsId: "signup_resume",
  },
  buddyInvite: {
    label: "Invite a buddy",
    microcopy: "One quiet companion, at your pace.",
    loadingLabel: "Sending…",
    defaultHierarchy: "primary",
    analyticsId: "buddy_invite",
  },
  newsletter: {
    label: "Send me the journal",
    microcopy: "A short letter, when there's something worth reading.",
    loadingLabel: "Subscribing…",
    defaultHierarchy: "secondary",
    analyticsId: "newsletter_subscribe",
  },
};

export function getCta(intent: CtaIntent): CtaDefinition {
  return CTA_LIBRARY[intent];
}
